import React, { useState, useEffect } from 'react';
import { User } from '../utils/auth';
import './Navbar.css';

// Header logo configuration
export interface HeaderLogoConfig {
  showLogo: boolean;
  showBrandName: boolean;
  order: "logo-first" | "brand-first";
  brandName: string;
  brandNameFontSize: string;
  logoImageSize: string;
  logoImageUrl: string;
}

// Navbar component with authentication support
interface NavbarProps {
  onSignInClick?: () => void;
  onSignUpClick?: () => void;
  onProfileClick?: () => void;
  onOrdersClick?: () => void;
  user?: User | null;
  headerLogoConfig?: Partial<HeaderLogoConfig>;
}

const Navbar: React.FC<NavbarProps> = ({ 
  onSignInClick, 
  onSignUpClick, 
  onProfileClick, 
  onOrdersClick,
  user,
  headerLogoConfig
}) => {
  // Default header logo config
  const defaultHeaderLogoConfig: HeaderLogoConfig = {
    showLogo: false,
    showBrandName: true,
    order: "brand-first",
    brandName: "CSZGLOBAL",
    brandNameFontSize: "1.5rem",
    logoImageSize: "40px",
    logoImageUrl: "",
  };

  const logoConfig = { ...defaultHeaderLogoConfig, ...headerLogoConfig };

  // Get logo image URL
  const getLogoImageUrl = (): string => {
    if (!logoConfig.logoImageUrl || logoConfig.logoImageUrl.trim() === "") {
      return "";
    }
    if (logoConfig.logoImageUrl.startsWith("http")) {
      return logoConfig.logoImageUrl;
    }
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8080";
    return `${apiUrl}${logoConfig.logoImageUrl}`;
  };

  // Render logo content based on config
  const renderLogoContent = () => {
    const logoImageUrl = getLogoImageUrl();
    const showLogo = logoConfig.showLogo && logoImageUrl !== "";
    const showBrand = logoConfig.showBrandName && logoConfig.brandName.trim() !== "";

    if (!showLogo && !showBrand) {
      return null;
    }

    const logoElement = showLogo ? (
      <img
        src={logoImageUrl}
        alt={logoConfig.brandName || "Logo"}
        style={{ width: logoConfig.logoImageSize, height: logoConfig.logoImageSize, objectFit: "contain" }}
      />
    ) : null;

    const brandElement = showBrand ? (
      <span className="logo-main" style={{ fontSize: logoConfig.brandNameFontSize }}>
        {logoConfig.brandName}
      </span>
    ) : null;

    if (logoConfig.order === "logo-first") {
      return (
        <>
          {logoElement}
          {brandElement}
        </>
      );
    } else {
      return (
        <>
          {brandElement}
          {logoElement}
        </>
      );
    }
  };
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
      <div className="navbar-container">
        {/* Logo */}
        <div className="navbar-logo">
          {renderLogoContent()}
        </div>

        {/* Desktop Navigation */}
        <div className="navbar-menu">
          <button 
            className="navbar-link" 
            onClick={() => scrollToSection('how-it-works')}
          >
            How it works
          </button>
          {/* Use cases dropdown - commented out */}
          {/* <div className="navbar-dropdown">
            <button className="navbar-link dropdown-trigger">
              Use cases
              <span className="dropdown-arrow">▼</span>
            </button>
            <div className="dropdown-menu">
              <button 
                className="dropdown-item" 
                onClick={() => scrollToSection('use-cases')}
              >
                For Casino Operators
              </button>
              <button 
                className="dropdown-item" 
                onClick={() => scrollToSection('use-cases')}
              >
                For Affiliate Managers
              </button>
              <button 
                className="dropdown-item" 
                onClick={() => scrollToSection('use-cases')}
              >
                For Influencers
              </button>
              <button 
                className="dropdown-item" 
                onClick={() => scrollToSection('use-cases')}
              >
                For First-time Founders
              </button>
            </div>
          </div> */}
          <button 
            className="navbar-link" 
            onClick={() => scrollToSection('key-differences')}
          >
            Key Differences
          </button>
          <button 
            className="navbar-link" 
            onClick={() => scrollToSection('pricing')}
          >
            Pricing
          </button>
          <button 
            className="navbar-link" 
            onClick={() => scrollToSection('faq')}
          >
            FAQ
          </button>
        </div>

        <div className="navbar-actions">
          {user && onOrdersClick && (
            <button 
              className="navbar-orders"
              onClick={onOrdersClick}
            >
              <span className="navbar-orders-icon">🧾</span>
              Orders
            </button>
          )}
          {user ? (
            <button 
              className="navbar-profile"
              onClick={onProfileClick}
              title={`Welcome, ${user.first_name} ${user.last_name}`}
            >
              <div className="navbar-profile-avatar">
                {user.first_name.charAt(0).toUpperCase()}
              </div>
              <span className="navbar-profile-name">{user.first_name} {user.last_name}</span>
            </button>
          ) : (
            <button 
              className="navbar-signin"
              onClick={onSignInClick}
            >
              SIGN IN
            </button>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="navbar-toggle"
          onClick={toggleMenu}
          aria-label="Toggle menu"
        >
          <span className={`hamburger ${isMenuOpen ? 'open' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
      </div>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${isMenuOpen ? 'open' : ''}`}>
        <button 
          className="mobile-link" 
          onClick={() => scrollToSection('how-it-works')}
        >
          How it works
        </button>
        {/* Use cases - commented out */}
        {/* <button 
          className="mobile-link" 
          onClick={() => scrollToSection('use-cases')}
        >
          Use cases
        </button> */}
        <button 
          className="mobile-link" 
          onClick={() => scrollToSection('key-differences')}
        >
          Key Differences
        </button>
        <button 
          className="mobile-link" 
          onClick={() => scrollToSection('pricing')}
        >
          Pricing
        </button>
        <button 
          className="mobile-link" 
          onClick={() => scrollToSection('faq')}
        >
          FAQ
        </button>
        {user && onOrdersClick && (
          <button 
            className="mobile-orders"
            onClick={onOrdersClick}
          >
            <span>🧾 Orders</span>
          </button>
        )}
        {user ? (
          <button 
            className="mobile-profile"
            onClick={onProfileClick}
          >
            <div className="mobile-profile-avatar">
              {user.first_name.charAt(0).toUpperCase()}
            </div>
            <span>Profile</span>
          </button>
        ) : (
          <button 
            className="mobile-signin"
            onClick={onSignInClick}
          >
            SIGN IN
          </button>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
